import {
  Checkbox,
  Feedback,
  Icon,
  P,
  Stack,
  Toggle,
} from '@fashionnovateam/nova-ui-toolkit';
import { useState } from 'react';
import {
  FormErrors,
  PurchaseOrderRevisionForm,
  UpdatePurchaseOrderRevisionForm,
} from './validation';
import { PurchaseOrder } from '../../../Lib/Types/purchase-order';
import { DueDateForm } from './DueDateForm';
import { DueDateId } from './formTargetId';

export type PoLevelFormProps = {
  purchaseOrder: PurchaseOrder;
  form: PurchaseOrderRevisionForm;
  updateForm: UpdatePurchaseOrderRevisionForm;
  disabled: boolean;
  errors: FormErrors | undefined;
};

export type VendorAcknowledgementProps = {
  updateForm: UpdatePurchaseOrderRevisionForm;
  form: PurchaseOrderRevisionForm;
  disabled: boolean;
  errors: FormErrors | undefined;
};

function VendorAcknowledgement(props: VendorAcknowledgementProps) {
  const { updateForm, form, disabled, errors } = props;
  return (
    <Feedback
      type="warning"
      title="Delivery Extension Request Notice"
      iconName="information/information-triangle"
    >
      <Stack direction="col" gap="md" className="text-xs text-base-disabled">
        <Stack direction="col" gap="none">
          Please Read Before Proceeding:
          <ul className="list-disc pl-5">
            <li>
              Submitting a delivery extension request does not guarantee
              approval or exemption from penalties.
            </li>
            <li>
              Fashion Nova reserves the right to impose chargebacks or cancel
              the Purchase Order (PO) if the revised delivery date falls outside
              the approved shipping window.
            </li>
            <li>
              All changes must be communicated at least 4 business days before
              your scheduled appointment.
            </li>
            <li>
              Failure to comply may result in shipment refusal, chargebacks, or
              order cancellation.
            </li>
          </ul>
          Only proceed if you understand and accept these terms.
        </Stack>
        <Stack
          direction="row"
          gap="sm"
          className="rounded items-center align-middle text-base-primary bg-black bg-opacity-[.04] p-3 -ml-7"
        >
          <Checkbox
            onChange={(e) =>
              updateForm((prev) => ({
                ...prev,
                due_date: {
                  ...prev.due_date,
                  vendor_acknowledged: e.target.checked,
                },
              }))
            }
            checked={!!form.due_date?.vendor_acknowledged}
            disabled={disabled}
          />
          <P>I understand and accept these terms.</P>
        </Stack>
        {errors?.['due_date.vendor_acknowledged'] && (
          <Stack direction="row" gap="xs" className="text-danger-700 -ml-7">
            <Icon name="alert/alert-circle" />
            You must accept to continue.
          </Stack>
        )}
      </Stack>
    </Feedback>
  );
}

type PurchaseOrderRevisionDueDate = NonNullable<
  PurchaseOrderRevisionForm['due_date']
>;

export function PoLevelForm(props: PoLevelFormProps) {
  const { purchaseOrder, form, updateForm, disabled, errors } = props;
  const [formPrevValue, setFormPrevValue] =
    useState<PurchaseOrderRevisionDueDate>({});

  const isFca = purchaseOrder.incoterm === 'FCA';

  const getDueDate = (
    checked: boolean,
    dueDate: PurchaseOrderRevisionDueDate | undefined,
  ) => {
    if (checked) {
      return formPrevValue;
    }
    if (dueDate !== undefined) {
      setFormPrevValue(dueDate);
    }
    return undefined;
  };

  return (
    <div className="flex flex-col gap-5" id={DueDateId}>
      <p className="text-lg font-medium text-base-primary">PO Level</p>
      <div className="flex items-center gap-3">
        <Toggle
          onChange={(e) =>
            updateForm((prev) => ({
              ...prev,
              due_date: getDueDate(e.target.checked, prev.due_date),
            }))
          }
          checked={!!form.due_date}
          disabled={disabled}
        />
        <span className="text-sm font-medium text-base-primary">
          {isFca ? 'FOB Ship Date Change' : 'Due Date Change'}
        </span>
      </div>
      {form.due_date && (
        <DueDateForm
          purchaseOrder={purchaseOrder}
          form={form.due_date}
          updateForm={(value) =>
            updateForm((prev) => ({
              ...prev,
              due_date: value,
            }))
          }
          disabled={disabled}
          errors={errors}
        />
      )}
      {typeof form.due_date?.vendor_acknowledged === 'boolean' && (
        <VendorAcknowledgement
          updateForm={updateForm}
          form={form}
          errors={errors}
          disabled={disabled}
        />
      )}
    </div>
  );
}
